import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Mail, Inbox, Send, Paperclip, Clock, User, 
  RefreshCw, Search, ChevronRight, X, Reply, Trash2,
  Download, AlertCircle, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEmailInboxPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [replyData, setReplyData] = useState({ subject: "", body: "" });
  const [composeData, setComposeData] = useState({ to: "", subject: "", body: "" });
  const [sendingReply, setSendingReply] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchEmails();
    }
  }, [navigate]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/emails/received`);
      // Handle the response - Resend returns { data: [...] }
      const emailData = response.data?.data || response.data || [];
      setEmails(Array.isArray(emailData) ? emailData : []);
    } catch (error) {
      console.error("Failed to fetch emails:", error);
      if (error.response?.status === 500) {
        toast.error("Email inbox not configured", {
          description: "Please set up Resend receiving in your dashboard"
        });
      } else {
        toast.error("Failed to load emails");
      }
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailDetails = async (emailId) => {
    try {
      const response = await axios.get(`${API}/emails/received/${emailId}`);
      setSelectedEmail(response.data);
      
      // Also fetch attachments
      try {
        const attachmentsRes = await axios.get(`${API}/emails/received/${emailId}/attachments`);
        setAttachments(attachmentsRes.data?.data || attachmentsRes.data || []);
      } catch {
        setAttachments([]);
      }
    } catch (error) {
      console.error("Failed to fetch email details:", error);
      toast.error("Failed to load email details");
    }
  };

  const handleReply = (email) => {
    setReplyData({
      subject: `Re: ${email.subject || "Your inquiry"}`,
      body: ""
    });
    setShowReplyModal(true);
  };

  const sendReply = async () => {
    if (!replyData.body.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API}/emails/send-reply`, {
        to: selectedEmail?.from?.email || selectedEmail?.from,
        subject: replyData.subject,
        body: replyData.body,
        original_email_id: selectedEmail?.id
      });
      toast.success("Reply sent successfully!");
      setShowReplyModal(false);
      setReplyData({ subject: "", body: "" });
    } catch (error) {
      console.error("Failed to send reply:", error);
      toast.error("Failed to send reply", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSendingReply(false);
    }
  };

  const sendEmail = async () => {
    if (!composeData.to.trim()) {
      toast.error("Please enter a recipient email");
      return;
    }
    if (!composeData.subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!composeData.body.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/emails/send`, {
        to: composeData.to,
        subject: composeData.subject,
        body: composeData.body
      });
      toast.success("Email sent successfully!");
      setShowComposeModal(false);
      setComposeData({ to: "", subject: "", body: "" });
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error("Failed to send email", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown date";
    try {
      return format(parseISO(dateStr), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateStr;
    }
  };

  const getFromEmail = (email) => {
    if (!email?.from) return "Unknown sender";
    if (typeof email.from === "string") return email.from;
    return email.from.email || email.from.name || "Unknown sender";
  };

  const getFromName = (email) => {
    if (!email?.from) return "";
    if (typeof email.from === "object" && email.from.name) {
      return email.from.name;
    }
    return "";
  };

  const filteredEmails = emails.filter(email => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const from = getFromEmail(email).toLowerCase();
    const subject = (email.subject || "").toLowerCase();
    return from.includes(query) || subject.includes(query);
  });

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#0a1628] pt-16">
      {/* Header */}
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin")} className="text-blue-400">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Mail size={24} className="text-blue-400" />
              <h1 className="text-xl font-semibold text-white">Email Inbox</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setShowComposeModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Send size={16} className="mr-2" />
              Compose
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchEmails}
              disabled={loading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email List */}
          <div className="lg:col-span-1 bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  placeholder="Search emails..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            {/* Email List */}
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw size={24} className="animate-spin mx-auto text-blue-400 mb-2" />
                  <p className="text-slate-400">Loading emails...</p>
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox size={48} className="mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No emails found</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Emails sent to hello@laguerre.co will appear here
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredEmails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => fetchEmailDetails(email.id)}
                      className={`w-full p-4 text-left hover:bg-white/5 transition-colors ${
                        selectedEmail?.id === email.id ? "bg-blue-500/10 border-l-2 border-blue-500" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            {getFromName(email) || getFromEmail(email)}
                          </p>
                          <p className="text-sm text-slate-400 truncate">
                            {email.subject || "(No subject)"}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {formatDate(email.created_at)}
                          </p>
                        </div>
                        {email.attachments?.length > 0 && (
                          <Paperclip size={14} className="text-slate-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Email Detail */}
          <div className="lg:col-span-2 bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
            {selectedEmail ? (
              <div className="h-full flex flex-col">
                {/* Email Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-xl font-medium text-white">
                      {selectedEmail.subject || "(No subject)"}
                    </h2>
                    <button
                      onClick={() => setSelectedEmail(null)}
                      className="text-slate-400 hover:text-white lg:hidden"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <User size={18} className="text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {getFromName(selectedEmail) || getFromEmail(selectedEmail)}
                        </p>
                        {getFromName(selectedEmail) && (
                          <p className="text-slate-400 text-xs">
                            {getFromEmail(selectedEmail)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-slate-400 text-xs ml-auto">
                      <Clock size={12} />
                      {formatDate(selectedEmail.created_at)}
                    </div>
                  </div>
                </div>

                {/* Email Body */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="prose prose-invert max-w-none">
                    {selectedEmail.html ? (
                      <div 
                        className="text-slate-300 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: selectedEmail.html }}
                      />
                    ) : selectedEmail.text ? (
                      <pre className="whitespace-pre-wrap text-slate-300 font-sans leading-relaxed">
                        {selectedEmail.text}
                      </pre>
                    ) : (
                      <p className="text-slate-500 italic">No content</p>
                    )}
                  </div>

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <p className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                        <Paperclip size={14} />
                        Attachments ({attachments.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {attachments.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={attachment.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-[#0a1628] rounded-lg text-sm text-slate-300 hover:bg-white/10 transition-colors"
                          >
                            <Download size={14} />
                            {attachment.filename || "Attachment"}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-white/10 flex gap-2">
                  <Button
                    onClick={() => handleReply(selectedEmail)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Reply size={16} className="mr-2" />
                    Reply
                  </Button>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <Mail size={64} className="mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">Select an email to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      <AnimatePresence>
        {showReplyModal && selectedEmail && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111c2e] rounded-xl border border-white/10 w-full max-w-2xl max-h-[80vh] overflow-hidden"
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Reply to Email</h3>
                <button
                  onClick={() => setShowReplyModal(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">To</label>
                  <Input
                    value={getFromEmail(selectedEmail)}
                    disabled
                    className="bg-[#0a1628] border-white/10 text-slate-400"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Subject</label>
                  <Input
                    value={replyData.subject}
                    onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                    className="bg-[#0a1628] border-white/10 text-white"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Message</label>
                  <Textarea
                    value={replyData.body}
                    onChange={(e) => setReplyData({ ...replyData, body: e.target.value })}
                    rows={8}
                    placeholder="Type your reply..."
                    className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-white/10 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReplyModal(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendReply}
                  disabled={sendingReply}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingReply ? (
                    <>
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Compose Email Modal */}
      <AnimatePresence>
        {showComposeModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111c2e] rounded-xl border border-white/10 w-full max-w-2xl max-h-[80vh] overflow-hidden"
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Send size={20} className="text-blue-400" />
                  Compose Email
                </h3>
                <button
                  onClick={() => setShowComposeModal(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">To</label>
                  <Input
                    type="email"
                    value={composeData.to}
                    onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                    placeholder="recipient@example.com"
                    className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Subject</label>
                  <Input
                    value={composeData.subject}
                    onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                    placeholder="Email subject"
                    className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Message</label>
                  <Textarea
                    value={composeData.body}
                    onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                    rows={10}
                    placeholder="Type your message..."
                    className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="text-xs text-slate-500">
                  Sending from: <span className="text-blue-400">hello@laguerre.co</span>
                </div>
              </div>

              <div className="p-4 border-t border-white/10 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowComposeModal(false); setComposeData({ to: "", subject: "", body: "" }); }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendEmail}
                  disabled={sendingEmail}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingEmail ? (
                    <>
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminEmailInboxPage;
